import {
  CIAuthTokenUsage,
  CICreditRangesType,
  Permission,
  ProductType,
  SubscriptionCIAuthToken,
} from '@localstack/types';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Skeleton,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import { CSSProperties, ReactElement, useMemo, useCallback, useState } from 'react';

import {
  formatDateTo,
  obfuscateWithStars,
  SubscriptionService,
  VALIDATION_RULES,
  useApiEffect,
  useApiGetter,
} from '@localstack/services';

import {
  CITokensUsageChart,
  ControlledTextField,
  ProgressButton,
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  BaseHeaderActionsMenu,
  ButtonWithDialog,
} from '@localstack/ui';

import { GridToolbar, GridValueFormatterParams } from '@mui/x-data-grid';
import { useForm } from 'react-hook-form';

import { useAuthProvider } from '~/hooks/useAuthProvider';
import { LicenseAssignmentPlanLabel } from '~/components/MembersAndLicenses/LicenseAssignmentPlanLabel';

import { AUTH_TOKENS_DATE_FORMAT } from '../../utils';
import { AssignmentBadgeBox } from '../LicenseAssigned';
import { TokenChip } from '../TokenChip';

import { UsageOverview } from './UsageOverview';

const DROPDOWN_OPTION_STYLE: CSSProperties = {
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  display: 'block',
};

const CardTitleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const CI_CREDIT_RANGES: CICreditRangesType = {
  green: [0, 70],
  orange: [70, 90],
  red: [90, 100],
  extra: [100, 200],
};

type RenameTokenDialogProps = {
  token: CIAuthTokenUsage;
  updateCiToken: (typeof SubscriptionService)['updateCiToken'];
  isCiKeyUpdating: boolean;
  onClose: () => void;
};

const RenameTokenDialog = ({
  token,
  updateCiToken,
  isCiKeyUpdating,
  onClose,
}: RenameTokenDialogProps): ReactElement => {
  const [value, setValue] = useState(token.name ?? '');
  return (
    <Box mt={1} display="flex" flexDirection="column" gap={2}>
      <TextField
        placeholder="CI Auth Token Name"
        size="small"
        variant="outlined"
        fullWidth
        value={value}
        onChange={({ target }) => setValue(target.value)}
        disabled={isCiKeyUpdating}
        required
      />
      <Box display="flex" justifyContent="flex-end" gap={1}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <ProgressButton
          variant="contained"
          color="primary"
          loading={isCiKeyUpdating}
          disabled={!value}
          onClick={async () => {
            await updateCiToken(token.id, { name: value });
            onClose();
          }}
        >
          Save
        </ProgressButton>
      </Box>
    </Box>
  );
};

type ManageCITokenProps = {
  subscriptionCITokens: SubscriptionCIAuthToken;
};

export const ManageSubscriptionCITokens = ({ subscriptionCITokens }: ManageCITokenProps): ReactElement => {
  const displayedQuota = subscriptionCITokens.quota !== 0 ? subscriptionCITokens.quota : '∞';
  const { data: usageSummary, isLoading: isUsageSummaryLoading } = useApiGetter(SubscriptionService, 'getUsage', [
    subscriptionCITokens.subscription_id,
  ]);

  const [selectedToken, setSelectedToken] = useState<{ id: string; name: string; rotatedFrom: string[] } | undefined>(
    undefined,
  );
  const creditsUsed = usageSummary?.usage_by_type[ProductType.CI_USAGE] || 0;

  const remainingCredits = typeof displayedQuota === 'string' ? displayedQuota : displayedQuota - creditsUsed;

  const { control, handleSubmit, formState } = useForm<{ name: string }>({ mode: 'all' });
  const { can } = useAuthProvider();
  const canWrite = can(Permission.WRITE_CI_TOKENS);

  const emulatorPill = (
    <AssignmentBadgeBox>
      <LicenseAssignmentPlanLabel emulator={subscriptionCITokens.emulator} family={subscriptionCITokens.plan_family} />
    </AssignmentBadgeBox>
  );

  const {
    createCiToken,
    deleteCiToken,
    updateCiToken,
    rotateCiToken,
    isLoading: isCiKeyUpdating,
  } = useApiEffect(SubscriptionService, ['createCiToken', 'deleteCiToken', 'updateCiToken', 'rotateCiToken'], {
    revalidate: ['listCiTokens'],
  });

  const submitHandler = useCallback(
    (data: { name: string }) => {
      if (!formState.isValid) return;
      createCiToken({
        subscription_id: subscriptionCITokens.subscription_id,
        name: data.name,
      });
    },
    [formState.isValid],
  );

  const columns = useMemo(
    (): GridColDef<CIAuthTokenUsage>[] => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
      },
      {
        field: 'token',
        headerName: 'Token',
        flex: 1,
        renderCell: ({ row }: GridRenderCellParams<CIAuthTokenUsage>) => <TokenChip token={row.token} />,
      },
      {
        field: 'usage',
        headerName: 'Invocations',
        width: 120,
      },
      {
        field: 'created',
        headerName: 'Created',
        width: 150,
        valueGetter: ({ row }: GridRenderCellParams<CIAuthTokenUsage>) => row.created,
        valueFormatter: ({ value }: GridValueFormatterParams<number | undefined>) =>
          value ? formatDateTo(value, AUTH_TOKENS_DATE_FORMAT) : '-',
      },
      {
        field: 'actions',
        headerName: '',
        width: 56,
        sortable: false,
        filterable: false,
        renderCell: ({ row }: GridRenderCellParams<CIAuthTokenUsage>) => {
          if (!canWrite) return null;
          return (
            <Box onClick={(e) => e.stopPropagation()}>
              <BaseHeaderActionsMenu>
                <ButtonWithDialog
                  componentType="MenuItem"
                  dialogTitle="Rename CI Auth Token"
                  hideDialogActions
                  modalMaxWidth="sm"
                  dialogContent={(closeDialog) => (
                    <RenameTokenDialog
                      token={row}
                      updateCiToken={updateCiToken}
                      isCiKeyUpdating={isCiKeyUpdating}
                      onClose={closeDialog}
                    />
                  )}
                >
                  Rename
                </ButtonWithDialog>
                <ButtonWithDialog
                  componentType="MenuItem"
                  dialogTitle="Regenerate CI Auth Token"
                  dialogContent={`Are you sure you want to regenerate this CI Auth Token (${row.name})? Any systems using the previous token will no longer be able to activate LocalStack.`}
                  confirmButtonText="Regenerate CI Auth Token"
                  confirmButtonProps={{ color: 'error' }}
                  cancelButtonText="Cancel"
                  onClick={() => rotateCiToken(row.id)}
                >
                  Regenerate
                </ButtonWithDialog>
                <ButtonWithDialog
                  componentType="MenuItem"
                  dialogTitle="Delete CI Auth Token"
                  dialogContent={`Are you sure you want to delete this CI Auth Token (${row.name})? Any systems using this CI Auth Token will no longer be able to activate LocalStack with this token.`}
                  confirmButtonText="Delete CI Auth Token"
                  confirmButtonProps={{ color: 'error' }}
                  cancelButtonText="Cancel"
                  onClick={() => deleteCiToken(row.id)}
                >
                  Delete
                </ButtonWithDialog>
              </BaseHeaderActionsMenu>
            </Box>
          );
        },
      },
    ],
    [canWrite, updateCiToken, deleteCiToken, rotateCiToken, isCiKeyUpdating],
  );

  return (
    <>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={
              <CardTitleRow>
                <Typography variant="h4">CI Auth Tokens</Typography>
                {emulatorPill}
              </CardTitleRow>
            }
            action={
              isUsageSummaryLoading ? (
                <Skeleton variant="text" width={280} />
              ) : (
                <Box display="flex" flexDirection="row" gap={1.5}>
                  <Typography>
                    Credits used: <b>{creditsUsed}</b>
                  </Typography>
                  <Typography>
                    Credits remaining: <b>{remainingCredits}</b>
                  </Typography>
                  <Typography color="text.secondary">
                    · Resets on {formatDateTo(usageSummary?.usage_period_end ?? 0, AUTH_TOKENS_DATE_FORMAT)}
                  </Typography>
                </Box>
              )
            }
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography>CI Auth Tokens can access all workspace data like Cloud Pods.</Typography>
              </Grid>
              <Grid item xs={12}>
                <UsageOverview
                  isLoading={isUsageSummaryLoading}
                  usageSummary={usageSummary}
                  creditsUsed={creditsUsed}
                  quota={displayedQuota}
                />
              </Grid>
              <Grid item xs={12}>
                <DataGrid
                  rows={subscriptionCITokens.tokens}
                  columns={columns}
                  rowHeight={56}
                  loading={isCiKeyUpdating}
                  components={{ Toolbar: GridToolbar }}
                  componentsProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 300 },
                      csvOptions: { disableToolbarButton: true },
                      printOptions: { disableToolbarButton: true },
                    },
                  }}
                  disableDensitySelector
                  disableSelectionOnClick
                  disableColumnSelector
                  sx={{ height: 400 }}
                />
              </Grid>
              {canWrite && (
                <Grid item xs={12}>
                  <form onSubmit={handleSubmit(submitHandler)}>
                    <Grid container spacing={1}>
                      <Grid item md={4} sm={6}>
                        <ControlledTextField
                          name="name"
                          control={control}
                          placeholder="CI Auth Token Name"
                          size="small"
                          variant="outlined"
                          fullWidth
                          disabled={isCiKeyUpdating}
                          rules={{
                            ...VALIDATION_RULES.safeStringRequired,
                            required: 'Provide a label to differentiate the token from others',
                          }}
                        />
                      </Grid>
                      <Grid item md={4} sm={6}>
                        <ProgressButton
                          variant="contained"
                          color="primary"
                          type="submit"
                          disabled={!formState.isValid}
                          loading={isCiKeyUpdating}
                        >
                          Generate New CI Auth Token
                        </ProgressButton>
                      </Grid>
                    </Grid>
                  </form>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {usageSummary && (
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title={
                <CardTitleRow>
                  <Typography variant="h4">CI Auth Tokens Usage</Typography>
                  {emulatorPill}
                </CardTitleRow>
              }
              action={
                <Box display="flex">
                  <Autocomplete
                    size="small"
                    options={subscriptionCITokens.tokens}
                    getOptionLabel={(token) => `${token.name} (${obfuscateWithStars(token.token)})`}
                    sx={{ width: '15em' }}
                    renderOption={(props, option) => (
                      <li {...props} style={DROPDOWN_OPTION_STYLE}>
                        {option.name} ({obfuscateWithStars(option.token)})
                      </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Highlight Auth Token" variant="outlined" />}
                    value={subscriptionCITokens.tokens.find((token) => token.id === selectedToken?.id) || null}
                    onChange={(_e, selectedItem) => {
                      if (!selectedItem) return setSelectedToken(undefined);

                      const token = subscriptionCITokens.tokens.find((item) => item.id === selectedItem.id);
                      if (!token) return setSelectedToken(undefined);

                      setSelectedToken({
                        id: token.id,
                        name: token.name || '',
                        rotatedFrom: token.rotation_history,
                      });
                    }}
                  />
                </Box>
              }
            />
            <CardContent>
              <CITokensUsageChart
                data={usageSummary}
                selectedToken={selectedToken}
                creditRangePercentages={CI_CREDIT_RANGES}
                creditsQuota={subscriptionCITokens.quota}
                subscriptionId={subscriptionCITokens.subscription_id}
              />
            </CardContent>
          </Card>
        </Grid>
      )}
    </>
  );
};
